import React, { useEffect, useState, useCallback } from "react";
import {
  ChevronLeft,
  UserPlus,
  Users,
  CheckCircle2,
  Loader2,
  AlertCircle,
  X,
  ShieldCheck,
} from "lucide-react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import AuthGateModal from "../components/AuthGateModal";
import { getSavedGolfers, saveGolfer } from "../services/golferservice";
import { useNavigate } from "react-router-dom";
import { getProfile } from "../services/profileService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = {
  name: "",
  passportNumber: "",
  phone: "",
  email: "",
  saveForFuture: true,
};

function validateGolfer(g) {
  const errors = {};
  if (!g.name?.trim()) errors.name = "Name is required.";
  if (!g.passportNumber?.trim())
    errors.passportNumber = "Passport number is required.";
  if (!g.phone?.trim()) errors.phone = "Phone number is required.";
  if (!g.email?.trim()) errors.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email.trim()))
    errors.email = "Enter a valid email address.";
  return errors;
}

/**
 * Dedicated step between "Proceed to Booking" and Payment Summary.
 * Handles all 3 scenarios in one page:
 *  - SC1 New golfers: the "Add a New Golfer" form below
 *  - SC2 Existing golfers: the "Select Existing Golfers" checklist
 *  - SC3 Combination: both sections feed the same roster simultaneously
 *
 * @param {{
 *   numPlayers: number;
 *   initialGolfers?: Array<object>; // roster from a previous visit, if the lead golfer navigated back
 *   onNext: (golfers: Array<object>) => void; // -> Payment Summary
 *   onBack?: () => void;
 * }} props
 */
export default function GolferDetailsPage({
  numPlayers,
  initialGolfers,
  onNext,
  onBack,
}) {
  const [user, setUser] = useState(auth.currentUser);
  const [showAuthGate, setShowAuthGate] = useState(!auth.currentUser);
  const navigate = useNavigate();

  const profileCache = new Map();

  async function fetchProfileCached(uid) {
    if (profileCache.has(uid)) return profileCache.get(uid);
    const profile = await getProfile(uid);
    profileCache.set(uid, profile);
    return profile;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setShowAuthGate(!u);
    });
    return unsubscribe;
  }, []);

  const [leadGolfer, setLeadGolfer] = useState(() => {
    const existing = initialGolfers?.find((g) => g.isLead);
    return existing || { ...emptyForm, isLead: true };
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [leadTouched, setLeadTouched] = useState(false);

  // Prefill from the account once it's available, without ever overwriting
  // something the lead golfer already typed.
  // useEffect(() => {
  //   if (!user) return;
  //   setLeadGolfer((prev) => ({
  //     ...prev,
  //     name: prev.name || user.displayName || "",
  //     email: prev.email || user.email || "",
  //   }));
  // }, [user]);

  useEffect(() => {
  if (!user) return;

  // Auth fields are available synchronously — prefill those immediately.
  setLeadGolfer((prev) => ({
    ...prev,
    name: prev.name || user.displayName || "",
    email: prev.email || user.email || "",
  }));

  // Passport/phone live on the Firestore profile doc, not the auth user —
  // fetch (or reuse the cached) profile and merge those in without ever
  // overwriting something the golfer already typed.
  let cancelled = false;
  (async () => {
    try {
      const profile = await fetchProfileCached(user.uid);
      if (cancelled) return;
      const pd = profile?.passport_details || {};
      console.log("Fetched profile for user:", user.uid, profile);
      setLeadGolfer((prev) => ({
        ...prev,
        passportNumber: prev.passportNumber || pd.passport_number || "",
        phone: prev.phone || profile?.mobile_number || pd.mobile_number || "",
      }));
    } catch (err) {
      console.error("Failed to load profile:", err);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [user]);

  const [savedGolfers, setSavedGolfers] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedError, setSavedError] = useState(null);
  const [showBuddiesModal, setShowBuddiesModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(
    () =>
      new Set(
        (initialGolfers || [])
          .filter((g) => g.id && !g.isLead)
          .map((g) => g.id),
      ),
  );

  const [addedGolfers, setAddedGolfers] = useState(() =>
    (initialGolfers || []).filter((g) => !g.id && !g.isLead),
  );
  const [newForm, setNewForm] = useState(emptyForm);
  const [newFormErrors, setNewFormErrors] = useState({});
  const [addingGolfer, setAddingGolfer] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const fetchSaved = useCallback(async () => {
    console.log("Fetching saved golfers for user:", numPlayers);
    if (!user) return;
    setLoadingSaved(true);
    setSavedError(null);
    try {
      const list = await getSavedGolfers(user.uid);
      setSavedGolfers(list);
    } catch (err) {
      console.error("Failed to load saved golfers:", err);
      setSavedError(err?.message || "Failed to load your saved golfers.");
    } finally {
      setLoadingSaved(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const totalSlots = numPlayers || 1;
  const slotsFilled = 1 + selectedIds.size + addedGolfers.length; // +1 for the lead
  const slotsRemaining = Math.max(0, totalSlots - slotsFilled);
  const rosterFull = slotsRemaining === 0;

  const toggleExisting = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (slotsFilled >= totalSlots) return prev; // already full
        next.add(id);
      }
      return next;
    });
  };

  const handleAddGolfer = async () => {
    const errors = validateGolfer(newForm);
    setNewFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (slotsFilled >= totalSlots) return;

    setAddingGolfer(true);
    try {
      let savedId = null;
      if (newForm.saveForFuture && user) {
        savedId = await saveGolfer(user.uid, {
          name: newForm.name.trim(),
          passportNumber: newForm.passportNumber.trim(),
          phone: newForm.phone.trim(),
          email: newForm.email.trim(),
        });
      }
      setAddedGolfers((prev) => [
        ...prev,
        {
          id: savedId, // null if the lead chose not to save it — still fine for this booking
          name: newForm.name.trim(),
          passportNumber: newForm.passportNumber.trim(),
          phone: newForm.phone.trim(),
          email: newForm.email.trim(),
        },
      ]);
      setNewForm(emptyForm);
      setNewFormErrors({});
      if (savedId) fetchSaved(); // so it also appears under "existing" right away
    } catch (err) {
      console.error("Failed to add golfer:", err);
      setNewFormErrors({
        _global: err?.message || "Failed to add this golfer. Try again.",
      });
    } finally {
      setAddingGolfer(false);
    }
  };

  const removeAdded = (index) =>
    setAddedGolfers((prev) => prev.filter((_, i) => i !== index));

  const handleContinue = () => {
    setLeadTouched(true);
    const errs = validateGolfer(leadGolfer);
    setLeadErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSubmitError(
        "Please complete your own golfer details before continuing.",
      );
      return;
    }
    if (slotsFilled !== totalSlots) {
      setSubmitError(
        `Add ${slotsRemaining} more golfer${slotsRemaining === 1 ? "" : "s"} to match your ${totalSlots}-player booking.`,
      );
      return;
    }
    setSubmitError(null);

    const selectedExistingGolfers = savedGolfers.filter((g) =>
      selectedIds.has(g.id),
    );
    const golfers = [
      { ...leadGolfer, isLead: true },
      ...selectedExistingGolfers.map((g) => ({ ...g, isLead: false })),
      ...addedGolfers.map((g) => ({ ...g, isLead: false })),
    ];

    onNext(golfers);
  };

  const hasSavedGolfers = !loadingSaved && savedGolfers.length > 0;
  const noSavedGolfers = !loadingSaved && savedGolfers.length === 0 && !savedError;
  const selectedBuddies = savedGolfers.filter((g) => selectedIds.has(g.id));

  function renderAddGolferForm(title) {
    return (
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide flex items-center gap-1.5">
            <UserPlus className="h-3.5 w-3.5" />
            {title}
          </p>
          <span className="text-xs text-gray-400">
            {slotsRemaining} slot{slotsRemaining === 1 ? "" : "s"} left
          </span>
        </div>

        {addedGolfers.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {addedGolfers.map((g, i) => (
              <span
                key={`new-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-300 pl-3 pr-1.5 py-1 text-xs font-semibold text-gray-800"
              >
                {g.name}
                <button
                  type="button"
                  onClick={() => removeAdded(i)}
                  className="rounded-full p-0.5 hover:bg-gray-200 text-gray-500"
                  aria-label={`Remove ${g.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {rosterFull ? (
          <p className="text-xs text-gray-400">
            Your roster is full for this booking — remove a golfer to add a
            different one.
          </p>
        ) : (
          <>
            <GolferFormFields
              value={newForm}
              onChange={(field, val) =>
                setNewForm((prev) => ({ ...prev, [field]: val }))
              }
              errors={newFormErrors}
            />
            <label className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <input
                type="checkbox"
                checked={newForm.saveForFuture}
                onChange={(e) =>
                  setNewForm((prev) => ({
                    ...prev,
                    saveForFuture: e.target.checked,
                  }))
                }
                className="rounded border-gray-300"
              />
              Save this golfer so I can select them again on future bookings
            </label>
            {newFormErrors._global && (
              <p className="text-xs text-red-600 mt-2">
                {newFormErrors._global}
              </p>
            )}
            <button
              type="button"
              onClick={handleAddGolfer}
              disabled={addingGolfer}
              className="mt-3 h-10 px-4 rounded-lg bg-emerald-800 hover:bg-emerald-900 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
            >
              {addingGolfer && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Golfer
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-28 mt-6 px-6 sm:px-0 max-w-2xl mx-auto">
      <div className="sticky top-20 z-10 bg-white pb-3 pt-1 -mt-1">
        {/* {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#0B2421] transition-colors mb-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        )} */}
        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
          Step 1 of 2 · Golfer Details
        </p>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Who's playing?
        </h1>
        <p className="text-sm text-gray-500">
          Add the {totalSlots} golfer{totalSlots > 1 ? "s" : ""} on this booking
          — select anyone you've added before, add new golfers, or mix both.
          Everyone needs a passport number, phone, and email on file for ferry
          check-in.
        </p>
      </div>

      <div className="space-y-4 mt-4">
        {/* Lead golfer — always slot 1, always required */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
              You (Lead Golfer)
            </p>
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
              Required
            </span>
          </div>
          <GolferFormFields
            value={leadGolfer}
            onChange={(field, val) =>
              setLeadGolfer((prev) => ({ ...prev, [field]: val }))
            }
            errors={leadTouched ? leadErrors : {}}
          />
        </div>

        {/* Golfing Buddies — select from golfers already on file, or add the first one */}
        {loadingSaved && (
          <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking your golfing buddies…
          </div>
        )}

        {savedError && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-2">
              Golfing Buddies
            </p>
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> {savedError}
            </p>
          </div>
        )}

        {hasSavedGolfers && (
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Golfing Buddies
              </p>
              {selectedBuddies.length > 0 && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
                  {selectedBuddies.length} selected
                </span>
              )}
            </div>
            {selectedBuddies.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedBuddies.map((g) => (
                  <span
                    key={g.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 border border-emerald-200 pl-3 pr-1.5 py-1 text-xs font-semibold text-emerald-900"
                  >
                    {g.name}
                    <button
                      type="button"
                      onClick={() => toggleExisting(g.id)}
                      className="rounded-full p-0.5 hover:bg-emerald-200 text-emerald-700"
                      aria-label={`Remove ${g.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-3">
                Pick from golfers you've added before.
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowBuddiesModal(true)}
              className="h-10 px-4 rounded-lg border border-emerald-700 text-emerald-800 hover:bg-emerald-50 text-sm font-semibold flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              Select Golfing Buddies
            </button>
          </div>
        )}

        {noSavedGolfers && renderAddGolferForm("Add Golfing Buddies")}

        {/* Add someone new, beyond your saved golfing buddies */}
        {hasSavedGolfers && renderAddGolferForm("Add a New Golfer")}

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {submitError}
          </div>
        )}

        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" />
          Passport and contact details are used only for ferry check-in and
          booking confirmations.
        </p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between z-20">
        <div>
          <p className="text-xs text-gray-500">Roster</p>
          <p className="text-lg font-bold text-gray-900">
            {slotsFilled} / {totalSlots} golfers
          </p>
        </div>
        <button
          type="button"
          onClick={handleContinue}
          className="h-12 px-8 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-semibold transition-colors"
        >
          Continue to Payment
        </button>
      </div>

      <AuthGateModal
        open={showAuthGate}
        onClose={() => setShowAuthGate(!auth.currentUser)}
        onAuthenticated={() => setShowAuthGate(false)}
      />

      <GolfingBuddiesModal
        open={showBuddiesModal}
        onClose={() => setShowBuddiesModal(false)}
        savedGolfers={savedGolfers}
        selectedIds={selectedIds}
        toggleExisting={toggleExisting}
        rosterFull={rosterFull}
      />
    </div>
  );
}

function GolfingBuddiesModal({
  open,
  onClose,
  savedGolfers,
  selectedIds,
  toggleExisting,
  rosterFull,
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0B2421]">Golfing Buddies</DialogTitle>
          <p className="text-sm text-gray-500">
            Select who's joining from the golfers you've added before.
          </p>
        </DialogHeader>
        <div className="space-y-2">
          {savedGolfers.map((g) => {
            const checked = selectedIds.has(g.id);
            const disabled = !checked && rosterFull;
            return (
              <button
                type="button"
                key={g.id}
                onClick={() => toggleExisting(g.id)}
                disabled={disabled}
                className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  checked
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-emerald-300"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <div
                  className={`h-5 w-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                    checked
                      ? "bg-emerald-600 border-emerald-600"
                      : "border-gray-300"
                  }`}
                >
                  {checked && <CheckCircle2 className="h-4 w-4 text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {g.name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    Passport {g.passportNumber} · {g.phone}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{g.email}</p>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-lg bg-emerald-800 hover:bg-emerald-900 text-white text-sm font-semibold"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GolferFormFields({ value, onChange, errors }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <FormField label="Full Name" error={errors.name}>
        <input
          type="text"
          value={value.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder="As shown on passport"
          className={fieldClass(errors.name)}
        />
      </FormField>
      <FormField label="Passport Number" error={errors.passportNumber}>
        <input
          type="text"
          value={value.passportNumber}
          onChange={(e) => onChange("passportNumber", e.target.value)}
          placeholder="e.g. K1234567"
          className={fieldClass(errors.passportNumber)}
        />
      </FormField>
      <FormField label="Phone Number" error={errors.phone}>
        <input
          type="tel"
          value={value.phone}
          onChange={(e) => onChange("phone", e.target.value)}
          placeholder="+65 9123 4567"
          className={fieldClass(errors.phone)}
        />
      </FormField>
      <FormField label="Email Address" error={errors.email}>
        <input
          type="email"
          value={value.email}
          onChange={(e) => onChange("email", e.target.value)}
          placeholder="name@example.com"
          className={fieldClass(errors.email)}
        />
      </FormField>
    </div>
  );
}

function FormField({ label, error, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function fieldClass(hasError) {
  return `w-full h-10 rounded-lg border bg-white px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 ${
    hasError ? "border-red-300" : "border-gray-200"
  }`;
}
